// src/components/common/NavigationBar.js
import { Link, NavLink } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { getMyProfile } from "../../api/ProfileApis";
import { useCart } from "../../context/CartContext";
import useLeadershipPageStatus from "../../hooks/useLeadershipPageStatus";
import logo from "../../assets/logo.png";

function NavigationBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // ── Leadership page status (hide link when disabled in backend) ──
  const { enabled: leadershipEnabled } = useLeadershipPageStatus();

  // ── Cart ──
  const { count: cartCount, ready: cartReady, toggleDrawer } = useCart();
  const showCartBadge = cartReady && cartCount > 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest(".mobile-menu")
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      setMenuOpen(false);
      setProfileDropdownOpen(false);
    };

    window.addEventListener("popstate", handleRouteChange);

    return () => {
      window.removeEventListener("popstate", handleRouteChange);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      const accessToken = localStorage.getItem("access_token");

      if (!accessToken) {
        if (isMounted) {
          setIsLoggedIn(false);
          setIsLoading(false);
        }
        return;
      }

      try {
        const response = await getMyProfile();

        if (isMounted) {
          if (response.status === 200) {
            setIsLoggedIn(true);
            setUser(response.data);
          } else {
            setIsLoggedIn(false);
            setUser(null);
          }
        }
      } catch (error) {
        if (isMounted) {
          setIsLoggedIn(false);
          setUser(null);
          if (error.response?.status === 401 || error.response?.status === 403) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            sessionStorage.removeItem("access_token");
            sessionStorage.removeItem("refresh_token");
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const isInstructor = user?.user_type_display === "Instructor";
  const isAdmin = user?.is_admin === true;

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    setIsLoggedIn(false);
    setUser(null);
    setProfileDropdownOpen(false);
    setMenuOpen(false);
    window.location.href = "/";
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavLinkClick = () => {
    setMenuOpen(false);
    setProfileDropdownOpen(false);
  };

  // Toggle the cart panel (does not navigate)
  const handleCartClick = () => {
    setMenuOpen(false);
    setProfileDropdownOpen(false);
    toggleDrawer?.();
  };

  const navLinkClass = ({ isActive }) =>
    isActive ? "nav-link nav-link--active" : "nav-link";

  if (isLoading) {
    return (
      <nav className="navbar">
        <div className="nav-left">
          <Link to="/" className="company-name">
            <img src={logo} alt="Hayducate" className="nav-logo" />
            <span className="nav-logo-text">Hayducate</span>
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar">
      <div className="nav-left">
        <Link to="/" className="company-name">
          <img src={logo} alt="Hayducate" className="nav-logo" />
          <span className="nav-logo-text">Hayducate</span>
        </Link>
      </div>

      <div
        className={`mobile-menu ${menuOpen ? "active" : ""}`}
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        <div className="bar"></div>
        <div className="bar"></div>
        <div className="bar"></div>
      </div>

      <div
        ref={mobileMenuRef}
        className={`nav-menu-container ${menuOpen ? "active" : ""}`}
      >
        <ul className={`nav-list ${menuOpen ? "active" : ""}`}>
          {/* ── 1. Public — Browse ── */}
          <li className="nav-item">
            <NavLink
              to="/courses"
              end
              onClick={handleNavLinkClick}
              className={navLinkClass}
            >
              Courses
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink
              to="/projects"
              end
              onClick={handleNavLinkClick}
              className={navLinkClass}
            >
              Projects
            </NavLink>
          </li>

          {/* ── 2. Public — Leadership (hidden when disabled) ── */}
          {leadershipEnabled !== false && (
            <li className="nav-item">
              <NavLink
                to="/leadership"
                end
                onClick={handleNavLinkClick}
                className={navLinkClass}
              >
                Leadership
              </NavLink>
            </li>
          )}

          {/* ── 3. Public — Shop ── */}
          <li className="nav-item">
            <NavLink
              to="/store"
              end
              onClick={handleNavLinkClick}
              className={navLinkClass}
            >
              Store
            </NavLink>
          </li>

          {isLoggedIn ? (
            <>
              {/* ── 4. Personal — My content ── */}
              <li className="nav-item">
                <NavLink
                  to="/enrolled-courses"
                  onClick={handleNavLinkClick}
                  className={navLinkClass}
                >
                  My Courses
                </NavLink>
              </li>

              {/* ── 5. Action — Cart (opens panel) ── */}
              <li className="nav-item">
                <button
                  type="button"
                  onClick={handleCartClick}
                  className="nav-link nav-cart-link"
                  aria-label={`Cart${showCartBadge ? ` — ${cartCount} items` : ""}`}
                >
                  <i className="fas fa-shopping-cart" aria-hidden="true" />
                  <span className="nav-cart-label">Cart</span>
                  {showCartBadge && (
                    <span className="nav-cart-badge" aria-hidden="true">
                      {cartCount > 99 ? "99+" : cartCount}
                    </span>
                  )}
                </button>
              </li>

              {/* ── 6. Account — profile dropdown ── */}
              <li className="nav-item profile-dropdown-container" ref={dropdownRef}>
                <button
                  className="profile-dropdown-toggle"
                  onClick={toggleProfileDropdown}
                  aria-expanded={profileDropdownOpen}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  {isInstructor && <span className="instructor-badge">Instructor</span>}
                  {isAdmin && <span className="admin-badge">Admin</span>}
                </button>

                {profileDropdownOpen && (
                  <div className="profile-dropdown-menu">
                    {/* ── Role info ── */}
                    <div className="user-role-info">
                      {isAdmin && isInstructor && (
                        <div className="multiple-roles">
                          <span className="role-badge admin-role">Administrator</span>
                          <span className="role-badge instructor-role">Instructor</span>
                        </div>
                      )}
                      {isAdmin && !isInstructor && (
                        <span className="role-badge admin-role">Administrator</span>
                      )}
                      {isInstructor && !isAdmin && (
                        <span className="role-badge instructor-role">Instructor</span>
                      )}
                    </div>

                    {/* ── Account ── */}
                    <Link to="/profile" className="dropdown-item" onClick={handleNavLinkClick}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      Profile
                      {isAdmin && isInstructor && (
                        <>
                          <span className="role-indicator">👑</span>
                          <span className="role-indicator">📚</span>
                        </>
                      )}
                      {isAdmin && !isInstructor && <span className="role-indicator">👑</span>}
                      {isInstructor && !isAdmin && <span className="role-indicator">📚</span>}
                    </Link>

                    <Link to="/orders" className="dropdown-item" onClick={handleNavLinkClick}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 2h12a2 2 0 0 1 2 2v18l-8-4-8 4V4a2 2 0 0 1 2-2z" />
                      </svg>
                      My Orders
                    </Link>

                    {/* ── Role tools (grouped, one divider) ── */}
                    {(isInstructor || isAdmin) && (
                      <>
                        <div className="dropdown-divider"></div>

                        {isInstructor && (
                          <Link to="/course-editor" className="dropdown-item instructor-item" onClick={handleNavLinkClick}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 19l7-7 3 3-7 7-3-3z" />
                              <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                              <path d="M2 2l7.586 7.586" />
                              <circle cx="11" cy="11" r="2" />
                            </svg>
                            Course Editor
                          </Link>
                        )}

                        {isAdmin && (
                          <Link to="/admin" className="dropdown-item admin-item" onClick={handleNavLinkClick}>
                            <i className="fas fa-crown admin-icon"></i>
                            Admin - Courses
                          </Link>
                        )}

                        {isAdmin && (
                          <Link to="/manage-store" className="dropdown-item admin-item" onClick={handleNavLinkClick}>
                            <i className="fas fa-store admin-icon"></i>
                            Admin - Store
                          </Link>
                        )}
                      </>
                    )}

                    {/* ── Session ── */}
                    <div className="dropdown-divider"></div>
                    <button onClick={handleLogout} className="dropdown-item logout-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16,17 21,12 16,7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </li>
            </>
          ) : (
            <>
              <li className="nav-item">
                <NavLink to="/login" onClick={handleNavLinkClick} className={navLinkClass}>
                  Login
                </NavLink>
              </li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default NavigationBar;